'use client'

import type { ReactNode } from 'react'
import { useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { z } from 'zod/v4'

import type { SchoolGrade } from '@helper/utils/domain'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ui/web/components'
import { AvatarSelector, typographyVariants } from '@ui/web/lib'
import { childAvatarList, SCHOOL_GRADE_OPTIONS } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'
import { SchoolGradeSelect } from '../../../lib/school-grade-select'
import { useStudentMutations } from '../use-student-mutations'

const GRADE_VALUES = SCHOOL_GRADE_OPTIONS.map((option) => option.value) as [SchoolGrade, ...SchoolGrade[]]

/**
 * Birth years offered, youngest first.
 *
 * PiBo's readers are roughly 4–14 years old (early readers through late-enrolled or repeating
 * primary students), so the list spans exactly that band and is derived from the current year —
 * a hardcoded list silently stops offering the youngest cohort every January.
 */
function getBirthYearOptions(): number[] {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: 11 }, (_, index) => currentYear - 4 - index)
}

const FormSchema = z.object({
  name: z.string().trim().min(1, 'Wajib diisi'),
  nickname: z.string().trim().max(50, 'Maksimal 50 karakter'),
  image: z.string().optional(),
  schoolClass: z.enum(GRADE_VALUES).nullable(),
  birthYear: z.number().int().nullable(),
})

/**
 * The subset of a student/child record this dialog needs to render its form. Both `Student` (a
 * `listChildren` row, from the table) and `getChildDetails`'s response satisfy this structurally —
 * callers pass either straight through, no adapter type required.
 */
export interface StudentSettingsStudent {
  id: string
  name: string
  nickname: string | null
  image: string | null
  pin: string | null
  schoolGrade: string | null
  birthYear: number | null
}

interface StudentSettingsDialogProps {
  student: StudentSettingsStudent
  /** Drives the copy, and which side of a Sambung Sekolah-Rumah connection the summary card shows:
   * a school dialog shows the linked "Orang tua"; a family dialog shows the linked "Sekolah". */
  type?: 'web' | 'school'
  onClose: () => void
}

export function StudentSettingsDialog({ student, type = 'school', onClose }: StudentSettingsDialogProps) {
  const noun = type === 'web' ? 'anak' : 'murid'
  // A child signs in with their own PIN plus the organization's code — "kode sekolah" for a school,
  // "kode keluarga" for a family.
  const pinLabel = type === 'web' ? 'PIN Anak' : 'PIN Murid'
  const orgCodeLabel = type === 'web' ? 'kode keluarga' : 'kode sekolah'

  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const editChild = useMutation(trpc.myOrganization.editChild.mutationOptions())
  const regeneratePin = useMutation(trpc.myOrganization.regenerateChildPin.mutationOptions())

  // Source of truth for who may touch identity fields, and the "sisi lawan" of a Sambung
  // Sekolah-Rumah connection to show — see `resolveChildEditContext` on the server. Fields default
  // to non-editable while this is loading rather than flashing editable-then-locked.
  const editContextQuery = useQuery(trpc.myOrganization.getChildEditContext.queryOptions({ userId: student.id }))
  const canEditName = editContextQuery.data?.permissions.name ?? false
  const canEditImage = editContextQuery.data?.permissions.image ?? false
  const canEditNickname = editContextQuery.data?.permissions.nickname ?? false
  const canEditBirthYear = editContextQuery.data?.permissions.birthYear ?? false
  const canEditPin = editContextQuery.data?.permissions.pin ?? false
  const connection = editContextQuery.data?.connection
  // A child editing their own account (isSelf) only ever gets name + avatar — nickname, Tingkat,
  // PIN, and the connection card are hidden outright rather than shown disabled, unlike the
  // dual-connected school-caller case below where staying visible-but-locked signals a handover.
  const isSelfEdit = editContextQuery.data?.isSelf ?? false

  // The dialog holds a row snapshot taken when the kebab was clicked, so a regenerated PIN has to
  // be tracked locally — refetching the table behind the dialog does not rewrite that snapshot.
  const [pin, setPin] = useState(student.pin)

  const form = useForm({
    defaultValues: {
      name: student.name,
      nickname: student.nickname ?? '',
      image: student.image ?? undefined,
      schoolClass: (student.schoolGrade as SchoolGrade | null) ?? null,
      birthYear: student.birthYear ?? null,
    } as z.input<typeof FormSchema>,
    validators: { onSubmit: FormSchema },
    onSubmit: async ({ value }) => {
      try {
        await editChild.mutateAsync({
          userId: student.id,
          data: {
            name: value.name.trim(),
            image: value.image ?? null,
            nickname: value.nickname.trim() ? value.nickname.trim() : null,
            schoolClass: value.schoolClass,
            birthYear: value.birthYear,
          },
        })
        await invalidate()
        toast.success(`Informasi ${noun} berhasil diperbarui.`)
        onClose()
      } catch (error) {
        toast.error(error instanceof Error ? error.message : `Gagal memperbarui informasi ${noun}`)
      }
    },
  })

  const isSubmitting = useStore(form.store, (s) => s.isSubmitting)
  const disabled = isSubmitting || editChild.isPending

  const handleRegeneratePin = async () => {
    try {
      const result = await regeneratePin.mutateAsync({ userId: student.id })
      setPin(result.pin)
      await invalidate()
      toast.success(`${pinLabel} berhasil dibuat ulang.`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal membuat ulang PIN')
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className='srd:flex srd:max-h-[95dvh] srd:w-full srd:max-w-md srd:flex-col srd:overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Pengaturan akun {noun}</DialogTitle>
          <DialogDescription>Perbarui informasi {noun}</DialogDescription>
        </DialogHeader>

        <form
          className='srd:flex srd:flex-col srd:gap-4'
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <FieldSet disabled={disabled}>
            <div className='srd:flex srd:w-full srd:items-center srd:justify-center srd:py-2'>
              <form.Field name='image'>
                {(field) => (
                  <AvatarSelector
                    handleAvatarSelectAction={(avatar) => field.handleChange(avatar.src)}
                    avatarData={childAvatarList}
                    initialAvatarKey={childAvatarList.find((a) => a.src === field.state.value)?.key ?? undefined}
                    size={64}
                    disabled={disabled || !canEditImage}
                  />
                )}
              </form.Field>
            </div>

            <form.Field name='name'>
              {(field) => (
                <Field>
                  <FieldLabel>Nama {noun}</FieldLabel>
                  <Input
                    placeholder='Nama lengkap'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={disabled || !canEditName}
                  />
                  {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )}
            </form.Field>

            {!isSelfEdit && (
              <form.Field name='nickname'>
                {(field) => (
                  <Field>
                    <FieldLabel>Panggilan akrab</FieldLabel>
                    <Input
                      value={field.state.value}
                      placeholder='Panggilan akrab'
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={disabled || !canEditNickname}
                    />
                    {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )}
              </form.Field>
            )}

            {!isSelfEdit && (
              <form.Field name='schoolClass'>
                {(field) => (
                  <Field>
                    <FieldLabel>Tingkat</FieldLabel>
                    <SchoolGradeSelect
                      value={field.state.value}
                      onValueChange={(next) => field.handleChange(next as SchoolGrade | null)}
                      disabled={disabled}
                      placeholder='Pilih tingkat'
                    />
                    {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )}
              </form.Field>
            )}

            {!isSelfEdit && (
              <form.Field name='birthYear'>
                {(field) => (
                  <Field>
                    <FieldLabel>Tahun lahir</FieldLabel>
                    <Select
                      value={field.state.value === null ? null : String(field.state.value)}
                      disabled={disabled || !canEditBirthYear}
                      onValueChange={(next: string | null) => {
                        // Birth year is optional, so pressing the year already picked clears it —
                        // same toggle as the Tingkat select above. Base UI fires this on every item
                        // press, including a press on the current value, which is what makes it work.
                        const current = field.state.value === null ? null : String(field.state.value)
                        field.handleChange(!next || next === current ? null : Number.parseInt(next, 10))
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Pilih tahun lahir'>
                          {(current: string | null) => current ?? 'Pilih tahun lahir'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {getBirthYearOptions().map((year) => (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )}
              </form.Field>
            )}
          </FieldSet>

          {!isSelfEdit && (
            <div className='srd:flex srd:flex-col srd:gap-2'>
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
                {pinLabel}
              </span>

              <div className='srd:flex srd:items-center srd:justify-between srd:gap-4'>
                <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>
                  {pin ?? '-'}
                </span>

                <Button
                  variant='link'
                  className='srd:h-fit srd:w-fit srd:p-0'
                  size='sm'
                  loading={regeneratePin.isPending}
                  disabled={regeneratePin.isPending || disabled || !canEditPin}
                  onClick={() => void handleRegeneratePin()}
                >
                  Buat ulang
                </Button>
              </div>

              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                Dipakai untuk login dengan {orgCodeLabel}. Buat ulang hanya jika diperlukan.
              </p>
            </div>
          )}

          {/* The "sisi lawan" of a Sambung Sekolah-Rumah connection — a school dialog shows who the
              linked parent is, a family dialog shows which school the child also belongs to. Never
              its own side: each app already knows its own organization. Hidden outright for a
              child's own self-edit dialog, which shows name + avatar only. */}
          {!isSelfEdit && type === 'school' && connection?.family && (
            <ParentConnectionCard connection={connection.family} />
          )}
          {!isSelfEdit && type === 'web' && connection?.school && (
            <SchoolConnectionCard connection={connection.school} />
          )}

          <DialogFooter>
            <Button variant='outline' onClick={onClose} disabled={disabled}>
              Batal
            </Button>
            <Button type='submit' loading={isSubmitting} disabled={disabled}>
              Perbarui
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ConnectionCardShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='srd:flex srd:flex-col srd:gap-2'>
      <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{label}</span>
      {children}
    </div>
  )
}

/** The school a connected child also belongs to — shown in the family dialog. */
function SchoolConnectionCard({
  connection,
}: {
  connection: { name: string; image: string | null; pin: string | null }
}) {
  return (
    <ConnectionCardShell label='Sekolah'>
      <div className='srd:flex srd:items-center srd:gap-3'>
        <Avatar profile='organization' size='lg'>
          <AvatarImage src={connection.image ?? undefined} />
          <AvatarFallback>{getInitials(connection.name)}</AvatarFallback>
        </Avatar>
        <div className='srd:flex srd:flex-col'>
          <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
            {connection.name}
          </span>
          <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
            Kode sekolah: {connection.pin ?? '-'}
          </span>
        </div>
      </div>
    </ConnectionCardShell>
  )
}

/** The parent a connected child is linked to — shown in the school dialog. */
function ParentConnectionCard({
  connection,
}: {
  connection: { parentName: string | null; parentEmail: string | null }
}) {
  const name = connection.parentName ?? 'Orang tua'
  return (
    <ConnectionCardShell label='Orang tua'>
      <div className='srd:flex srd:items-center srd:gap-3'>
        <Avatar profile='user' size='lg'>
          <AvatarFallback>{getInitials(name)}</AvatarFallback>
        </Avatar>
        <div className='srd:flex srd:flex-col'>
          <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>{name}</span>
          {connection.parentEmail && (
            <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
              {connection.parentEmail}
            </span>
          )}
        </div>
      </div>
    </ConnectionCardShell>
  )
}
